import React, { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import PageHeader from '../PageHeader';
import RuleCard from './RuleCard';
import RuleModal from './RuleDrawer';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import {
  emptyRuleForm,
  formToRule,
  ruleToForm,
  findDuplicateRule,
  DUPLICATE_RULE_MESSAGE,
  RULES_TABLE_GRID_COLS,
  RULES_TABLE_MIN_WIDTH,
} from './constants';
import {
  getAlertRules,
  createAlertRule,
  updateAlertRule,
  toggleAlertRule,
  deleteAlertRule,
} from '../../utils/api';

const AlertRulesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rules, setRules] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyRuleForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { loadRules({ showPageLoader: true }); }, []);

  const loadRules = async ({ showPageLoader = false } = {}) => {
    try {
      if (showPageLoader) setInitialLoading(true);
      const data = await getAlertRules();
      setRules(Array.isArray(data) ? data : []);
    } catch {
      showSnackbar('Failed to load rules', 'error');
      if (showPageLoader) setRules([]);
    } finally {
      if (showPageLoader) setInitialLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const visibleRules = useMemo(() => rules, [rules]);
  const hasRules = visibleRules.length > 0;

  const openAddModal = () => {
    setModalMode('add');
    setEditingId(null);
    setForm(emptyRuleForm());
    setModalOpen(true);
  };

  const openEditModal = (rule) => {
    setModalMode('edit');
    setEditingId(rule.id);
    setForm(ruleToForm(rule));
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditingId(null); };

  const handleSaveRule = async () => {
    try {
      const ruleData = formToRule(form);
      if (findDuplicateRule(ruleData, rules, modalMode === 'edit' ? editingId : null)) {
        showSnackbar(DUPLICATE_RULE_MESSAGE, 'error');
        return;
      }
      if (modalMode === 'edit' && editingId) {
        await updateAlertRule(editingId, ruleData);
        showSnackbar('Rule updated successfully');
      } else {
        await createAlertRule(ruleData);
        showSnackbar('Rule created successfully');
      }
      closeModal();
      await loadRules();
    } catch (err) {
      showSnackbar(err.message || 'Failed to save rule', 'error');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    const nextStatus = !currentStatus;
    setTogglingId(id);
    setRules((prev) => prev.map((r) => r.id === id ? { ...r, is_active: nextStatus } : r));
    try {
      await toggleAlertRule(id, nextStatus);
      showSnackbar(`Rule ${nextStatus ? 'activated' : 'deactivated'} successfully`);
    } catch {
      setRules((prev) => prev.map((r) => r.id === id ? { ...r, is_active: currentStatus } : r));
      showSnackbar('Failed to toggle rule', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDuplicate = (rule) => {
    const dup = ruleToForm(rule);
    dup.is_active = false;
    dup.name = `${dup.name} (Copy)`;
    setModalMode('add');
    setEditingId(null);
    setForm(dup);
    setModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAlertRule(deleteTarget.id);
      showSnackbar('Rule deleted successfully');
      setDeleteTarget(null);
      await loadRules();
    } catch {
      showSnackbar('Failed to delete rule', 'error');
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Container maxWidth="lg" sx={{ width: '100%', maxWidth: '100%', px: { xs: 1.5, sm: 3 } }}>
        <PageHeader
          title="Alert Setting"
          subtitle="Configure attendance-based notification rules for your company"
        />

        <Card variant="outlined" sx={{ borderRadius: { xs: 2, sm: 3 }, overflow: 'hidden', mb: 2 }}>
          {hasRules && (
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 1.5, sm: 2 },
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#40518A', fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                Rules
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAddModal}
                size="small"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Add Rule
              </Button>
            </Box>
          )}

          {visibleRules.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                No rules configured yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Get started by creating your first alert rule.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAddModal}>
                Add Rule
              </Button>
            </Box>
          ) : (
            <Box sx={{ overflowX: { sm: 'auto' }, WebkitOverflowScrolling: 'touch' }}>
              {hasRules && (
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'grid' },
                    gridTemplateColumns: RULES_TABLE_GRID_COLS,
                    gap: 2,
                    px: 3,
                    py: 1.25,
                    bgcolor: '#f8fafc',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    minWidth: RULES_TABLE_MIN_WIDTH,
                  }}
                >
                  {['NAME', 'DESCRIPTION', 'STATUS', 'CREATED ON', 'CREATED BY', 'UPDATED BY', 'ACTIONS'].map((col) => (
                    <Typography key={col} variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
                      {col}
                    </Typography>
                  ))}
                </Box>
              )}
              <Stack>
                {visibleRules.map((rule) => (
                  <RuleCard
                    key={rule.id}
                    rule={rule}
                    toggling={togglingId === rule.id}
                    onToggleActive={handleToggleActive}
                    onEdit={openEditModal}
                    onDuplicate={handleDuplicate}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Card>
      </Container>

      <RuleModal
        open={modalOpen}
        mode={modalMode}
        form={form}
        onChange={setForm}
        onClose={closeModal}
        onSave={handleSaveRule}
      />

      <DeleteConfirmDialog
        open={Boolean(deleteTarget)}
        ruleName={deleteTarget?.name || ''}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertRulesPage;
